import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Formik } from 'formik';
import { Autocomplete, createTheme, Divider, FormHelperText, Grid2, IconButton, TextField, ThemeProvider, Typography, useTheme } from '@mui/material';
import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IoMdClose } from 'react-icons/io';
import { useAddDocumentMutation } from 'store/slices/apis/documentsSlice';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAddMonteCarloSimulationMutation } from 'store/slices/apis/monteCarloSimulationSlice';
import {ReactComponent as CrossIcon } from "assets/PwC_Funct_Icons_Close_Outline_Black_RGB.svg"

const activities = [
    { value: "All" },
    { value: "Engineering" },
    { value: "Procurement" },
    { value: "Construction" }
];

const distributionMethods = [
    { value: "Normal" },
    { value: "Uniform" },
    { value: "Triangular" }
];

const durationDistributions = Array.from({ length: 10 }, (_, i) => {
    const value = `${(i + 1) * 10}`;
    return { label: 'P'+value, value };
});

export default function AddMoteCarloSimulation(props) {
    const { open, cancel, openNotification, rowData, isViewSimulation } = props;
    
    const newtheme = useTheme();
    const { projectId } = useParams();

    const navigate = useNavigate();

    const [addSimulationFun, addSimulationRes] = useAddMonteCarloSimulationMutation();

    const [formData, setFormData] = useState({
        projectId: projectId,
        activityType: rowData?.activityType,
        minimum: rowData?.minimum,
        mostLikely: rowData?.mostLikely,
        maximum: rowData?.maximum,
        numberOfIterations: rowData?.numberOfIterations,
        distributionMethod: rowData?.distributionMethod,
        taskMostLikelyDurationSource: rowData?.taskMostLikelyDurationSource,
        taskMinDurationSource: rowData?.taskMinDurationSource,
        taskMaxDurationSource: rowData?.taskMaxDurationSource,
        status: 'Running'
        // runDate: ''
    })

    const theme = createTheme({
        components: {

            MuiTextField: {
                styleOverrides: {
                    root: {
                        border: '2px solid #e6e6e6', '& .MuiFilledInput-input': {
                            backgroundColor: '#fff',
                            color: '#252525',
                            fontSize: '16px'
                        },
                        '& .MuiFilledInput-root::before': {
                            borderBottom: 'none'
                        },
                        '& .MuiFilledInput-root::after': {
                            borderBottom: `2px solid ${newtheme?.customSettings?.themeColor}`
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: '#696969',
                            fontSize: '14px'
                        },
                        '& .MuiFormLabel-root': {
                            fontSize: '15px'
                        },
                        '& .MuiFilledInput-root.Mui-disabled:before': {
                            borderBottom: 'none'
                        },
                        '& .MuiFilledInput-root': {
                            backgroundColor: '#fff'
                        },
                        '& .MuiFilledInput-input': {
                            backgroundColor: 'none',
                            fontSize: '14px'
                        },
                        "& .MuiInputLabel-root.Mui-disabled": {
                            color: "#696969",
                        },
                        "& .MuiFilledInput-root.Mui-disabled": {
                            backgroundColor: "#fff",
                            color: "inherit",
                        },
                        "& .MuiFilledInput-input.Mui-disabled": {
                            WebkitTextFillColor: "#696969",
                            opacity: "none",
                        },
                    },
                },
            }
        },
    });

    const validationSchema = Yup.object({
        activityType: Yup.string().required("Activity type field is required."),
        minimum: Yup.number()
            .typeError('Must be a number')
            .min(1, 'Value must be greater than zero')
            .required('Minimum field is required'),
        mostLikely: Yup.number()
            .typeError('Must be a number')
            .min(1, 'Value must be greater than zero')
            .required('Most Likely field is required'),
        maximum: Yup.number()
            .typeError('Must be a number')
            .min(1, 'Value must be greater than zero')
            .required('Maximum field is required'),
        numberOfIterations: Yup.number()
            .typeError('Must be a number')
            .min(1, 'Value must be greater than zero')
            .max(10000, 'Value must be less than 10000')
            .required('Number of iterations field is required'),
        distributionMethod: Yup.string().required("Distribution method field is required."),
        taskMostLikelyDurationSource: Yup.string().required("Task most likely duration field is required."),
        taskMinDurationSource: Yup.string().required("Task minimum duration field is required."),
        taskMaxDurationSource: Yup.string().required("Task maximum duration field is required."),
        // runDate: Yup.date()
        //     .required("Run Date field is required")
        //     .nullable()
        //     .typeError("Run Date must be a valid date"),
    });


    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        
        addSimulationFun(values)
    };

    React.useEffect(() => {
        if (addSimulationRes?.isSuccess) {
            if (addSimulationRes?.data?.status == 'success') {
                openNotification(addSimulationRes?.data?.message, 'success')
            } else {
                openNotification(addSimulationRes?.data?.message, 'error')
            }
            cancel();
        }
    }, [addSimulationRes?.isSuccess]);

    React.useEffect(() => {
        if (isViewSimulation) {
            
            setFormData({
                activityType: rowData?.activityType || "",
                minimum: rowData?.minimum || "",
                mostLikely: rowData?.mostLikely || "",
                maximum: rowData?.maximum || "",
                numberOfIterations: rowData?.numberOfIterations || "",
                distributionMethod: rowData?.distributionMethod || "",
                taskMinDurationSource: rowData?.taskMinDurationSource || "",
                taskMostLikelyDurationSource: rowData?.taskMostLikelyDurationSource || "",
                taskMaxDurationSource: rowData?.taskMaxDurationSource || "",
            });
        }
    }, [isViewSimulation]);

    React.useEffect(() => {
        if (addSimulationRes?.isError) {
            openNotification('Something went wrong!', 'error')
        }
    }, [addSimulationRes?.isError]);
    return (
        <React.Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={'md'}
                open={open}
                sx={{
                    '& .MuiDialog-paper': {
                        overflowY: 'inherit'
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '20px', color: '#696969' }}>{isViewSimulation ? "Monte Carlo Simulation" : 'New Monte Carlo Simulation'}</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={cancel}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                     {/* <IoMdClose style={{ fontSize: '20px' }} /> */}
                    <CrossIcon style={{ fontSize: "20px", width:"20px", height:"20px", fill:"#808080" }} />
                </IconButton>
                <Divider sx={{ w: 1 }} />
                <DialogContent>
                    <ThemeProvider theme={theme}>
                        <Box sx={{ mt: 1 }}>
                            <Formik
                                initialValues={formData}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, setTouched }) => (
                                    <form onSubmit={handleSubmit}>

                                        <Grid2 container spacing={2}>
                                            {/* <Grid2 size={6}>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        label="Run Date"
                                                        value={
                                                            values?.runDate ? dayjs(values.runDate) : null
                                                        }
                                                        format="DD-MM-YYYY"
                                                        onChange={(newValue) => {
                                                            handleChange({
                                                                target: {
                                                                    name: "runDate",
                                                                    value: newValue ? newValue : "",
                                                                },
                                                            });
                                                        }}
                                                        onBlur={() =>
                                                            setTouched({ ...touched, runDate: true })
                                                        }
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                variant: "filled",
                                                                InputLabelProps: {
                                                                    shrink: true,
                                                                    style: { fontWeight: 400 },
                                                                },
                                                                inputProps: {
                                                                    style: { height: "20px" },
                                                                },
                                                                placeholder: "DD/MM/YYYY",
                                                                sx: {
                                                                    input: {
                                                                        "&::placeholder": {
                                                                            fontSize: 14,
                                                                            fontWeight: 400,
                                                                        },
                                                                    },
                                                                    "& .MuiFormLabel-asterisk": {
                                                                        color: "red",
                                                                        fontSize: 20,
                                                                    },
                                                                },
                                                                onKeyDown: (e) => e.preventDefault(),
                                                            },
                                                        }}
                                                    />
                                                    {touched?.runDate && errors?.runDate && (
                                                        <FormHelperText
                                                            sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                        >{`${errors?.runDate}`}</FormHelperText>
                                                    )}
                                                </LocalizationProvider>
                                            </Grid2> */}
                                            <Grid2 size={6}>
                                                <TextField
                                                    id="outlined-multiline-static2"
                                                    label="Number of iterations"
                                                    name="numberOfIterations"
                                                    placeholder='Enter Number of iterations'
                                                    fullWidth
                                                    value={values?.numberOfIterations}
                                                    onChange={handleChange}
                                                    onBlur={() => setTouched({ ...touched, numberOfIterations: true })}
                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                    variant="filled"
                                                    disabled={isViewSimulation}
                                                />
                                                {touched?.numberOfIterations && errors?.numberOfIterations &&
                                                    <FormHelperText sx={{ color: '#C52A1A', fontSize: '12px' }}>{`${errors?.numberOfIterations}`}</FormHelperText>
                                                }
                                            </Grid2>

                                            <Grid2 size={6}>
                                                <Autocomplete
                                                    options={distributionMethods}
                                                    disabled={isViewSimulation}
                                                    getOptionLabel={(option) => option?.value}
                                                    value={distributionMethods.find((option) => option?.value === values?.distributionMethod) || null}
                                                    onChange={(_, newValue) => setFieldValue("distributionMethod", newValue ? newValue?.value : "")}
                                                    onBlur={() => setTouched({ ...touched, distributionMethod: true })}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Distribution Method"
                                                            placeholder='Select Distribution Method'
                                                            name="distributionMethod"
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            variant="filled"
                                                        />
                                                    )}
                                                    ListboxProps={{
                                                        sx: { fontSize: "13px" },
                                                    }}
                                                />
                                                {touched?.distributionMethod && errors?.distributionMethod &&
                                                    <FormHelperText sx={{ color: '#C52A1A', fontSize: '12px' }}>{`${errors?.distributionMethod}`}</FormHelperText>
                                                }
                                            </Grid2>
                                            <Grid2 size={6}>
                                                <Autocomplete
                                                    options={activities}
                                                    disabled={isViewSimulation}
                                                    getOptionLabel={(option) => option?.value}
                                                    value={activities?.find((option) => option?.value === values?.activityType) || null}
                                                    onChange={(_, newValue) => setFieldValue("activityType", newValue ? newValue?.value : "")}
                                                    onBlur={() => setTouched({ ...touched, activityType: true })}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Activity Type"
                                                            placeholder='Select Activity Type'
                                                            name="activityType"
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            variant="filled"
                                                        />
                                                    )}
                                                    ListboxProps={{
                                                        sx: { fontSize: "13px" },
                                                    }}
                                                />
                                                {touched?.activityType && errors?.activityType &&
                                                    <FormHelperText sx={{ color: '#C52A1A', fontSize: '12px' }}>{`${errors?.activityType}`}</FormHelperText>
                                                }

                                            </Grid2>
                                            <Grid2 size={6}></Grid2>
                                            <Grid2 size={12}>
                                                <Typography variant={"subtitle1"} sx={{ mb:'-10px' }}>
                                                    Probablity Distribution % For Activities
                                                </Typography>
                                            </Grid2>
                                            <Grid2 size={4}>
                                                <TextField
                                                    id="outlined-multiline-static2"
                                                    label="Optimistic"
                                                    name="minimum"
                                                    placeholder='Enter Minimum'
                                                    fullWidth
                                                    value={values?.minimum}
                                                    onChange={handleChange}
                                                    onBlur={() => setTouched({ ...touched, minimum: true })}
                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                    variant="filled"
                                                    disabled={isViewSimulation}
                                                />
                                                {touched?.minimum && errors?.minimum &&
                                                    <FormHelperText sx={{ color: '#C52A1A', fontSize: '12px' }}>{`${errors?.minimum}`}</FormHelperText>
                                                }
                                            </Grid2>

                                            <Grid2 size={4}>
                                                <TextField
                                                    id="outlined-multiline-static2"
                                                    label="Most Likely"
                                                    name="mostLikely"
                                                    placeholder='Enter Most Likely'
                                                    fullWidth
                                                    value={values?.mostLikely}
                                                    onChange={handleChange}
                                                    onBlur={() => setTouched({ ...touched, mostLikely: true })}
                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                    variant="filled"
                                                    disabled={isViewSimulation}
                                                />
                                                {touched?.mostLikely && errors?.mostLikely &&
                                                    <FormHelperText sx={{ color: '#C52A1A', fontSize: '12px' }}>{`${errors?.mostLikely}`}</FormHelperText>
                                                }
                                            </Grid2>

                                            <Grid2 size={4}>
                                                <TextField
                                                    id="outlined-multiline-static2"
                                                    label="Pessimistic"
                                                    name="maximum"
                                                    placeholder='Enter Maximum'
                                                    fullWidth
                                                    value={values?.maximum}
                                                    onChange={handleChange}
                                                    onBlur={() => setTouched({ ...touched, maximum: true })}
                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                    variant="filled"
                                                    disabled={isViewSimulation}
                                                />
                                                {touched?.maximum && errors?.maximum &&
                                                    <FormHelperText sx={{ color: '#C52A1A', fontSize: '12px' }}>{`${errors?.maximum}`}</FormHelperText>
                                                }
                                            </Grid2>
                                            <Grid2 size={12}>
                                                <Typography variant={"subtitle1"} sx={{ mb:'-10px' }}>
                                                    Output Configuration
                                                </Typography>
                                            </Grid2>
                                            <Grid2 size={4}>
                                                <Autocomplete
                                                    options={durationDistributions}
                                                    disabled={isViewSimulation}
                                                    getOptionLabel={(option) => option?.label}
                                                    value={durationDistributions.find((option) => option.value == values?.taskMinDurationSource) || null}
                                                    onChange={(_, newValue) => setFieldValue("taskMinDurationSource", newValue ? newValue.value : "")}
                                                    onBlur={() => setTouched({ ...touched, taskMinDurationSource: true })}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Optmistic Duration"
                                                            placeholder='Select Optmistic Duration'
                                                            name="taskMinDurationSource"
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            variant="filled"
                                                        />
                                                    )}
                                                    ListboxProps={{
                                                        sx: { fontSize: "13px" },
                                                    }}
                                                />
                                                {touched?.taskMinDurationSource && errors?.taskMinDurationSource &&
                                                    <FormHelperText sx={{ color: '#C52A1A', fontSize: '12px' }}>{`${errors?.taskMinDurationSource}`}</FormHelperText>
                                                }
                                            </Grid2>

                                            <Grid2 size={4}>
                                                <Autocomplete
                                                    options={durationDistributions}
                                                    disabled={isViewSimulation}
                                                    getOptionLabel={(option) => option?.label}
                                                    value={durationDistributions.find((option) => option.value == values?.taskMostLikelyDurationSource) || null}
                                                    onChange={(_, newValue) => setFieldValue("taskMostLikelyDurationSource", newValue ? newValue.value : "")}
                                                    onBlur={() => setTouched({ ...touched, taskMostLikelyDurationSource: true })}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Most Likely Duration"
                                                            placeholder='Select Most Likely Duration'
                                                            name="taskMostLikelyDurationSource"
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            variant="filled"
                                                        />
                                                    )}
                                                    ListboxProps={{
                                                        sx: { fontSize: "13px" },
                                                    }}
                                                />
                                                {touched?.taskMostLikelyDurationSource && errors?.taskMostLikelyDurationSource &&
                                                    <FormHelperText sx={{ color: '#C52A1A', fontSize: '12px' }}>{`${errors?.taskMostLikelyDurationSource}`}</FormHelperText>
                                                }
                                            </Grid2>

                                            <Grid2 size={4}>
                                                <Autocomplete
                                                    options={durationDistributions}
                                                    disabled={isViewSimulation}
                                                    getOptionLabel={(option) => option?.label}
                                                    value={durationDistributions.find((option) => option.value == values?.taskMaxDurationSource) || null}
                                                    onChange={(_, newValue) => setFieldValue("taskMaxDurationSource", newValue ? newValue.value : "")}
                                                    onBlur={() => setTouched({ ...touched, taskMaxDurationSource: true })}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Pessimistic Duration"
                                                            placeholder='Select Pessimistic Duration'
                                                            name="taskMaxDurationSource"
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            variant="filled"
                                                        />
                                                    )}
                                                    ListboxProps={{
                                                        sx: { fontSize: "13px" },
                                                    }}
                                                />
                                                {touched?.taskMaxDurationSource && errors?.taskMaxDurationSource &&
                                                    <FormHelperText sx={{ color: '#C52A1A', fontSize: '12px' }}>{`${errors?.taskMaxDurationSource}`}</FormHelperText>
                                                }
                                            </Grid2>
                                            {!isViewSimulation &&
                                                <Grid2 size={12}>
                                                    <Button
                                                        type='submit'
                                                        loading={addSimulationRes?.isLoading}
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: `${newtheme?.customSettings?.themeColor}`,
                                                            color: '#FFF', padding: '7px 20px',
                                                            fontWeight: '500', textTransform: 'none',
                                                            fontSize: '16px', float: 'right'
                                                        }}>
                                                        Run
                                                    </Button>
                                                </Grid2>
                                            }
                                        </Grid2>
                                    </form>
                                )}
                            </Formik>
                        </Box>
                    </ThemeProvider>
                </DialogContent>
            </Dialog>
        </React.Fragment >
    );
}